/**
 * shared/protocol/codec.ts — binary codecs for CMD and SNAP [NET §6.1-§6.5].
 *
 * OWNER: NETCODE agent.  Little-endian; every binary message starts with a u8 MsgType.
 * Decoders accept an ArrayBuffer or any Uint8Array view (byteOffset honoured: Node `Buffer`s from
 * `ws` are pooled slices) [NET §9].
 *
 * ---- CMD (client -> server) [NET §6.1, extended] -------------------------------------------
 *   u8  type = 0x01
 *   u32 ackSnapTick
 *   u32 firstCmdNumber          cmds are consecutive: cmds[i].number = first + i
 *   u8  count                   1..MAX_CMDS_PER_PACKET
 *   per cmd (24 bytes, absolute — delta coding is not worth it at this size, NET §6.1):
 *     i32 serverTime            explicit (ARCHITECTURE §9.1); the server re-derives it from the
 *                               number + cmdTimeBase and never trusts this value for time
 *     u32 buttons               Button bits (resolved intents)
 *     u16 angles[3]             ANGLE2SHORT [Q3 q_shared.h]
 *     i8  forwardmove, rightmove  -127..127
 *     u8  weapon                WeaponId (0 = keep)
 *     u8  meleeChargeDist       0..255 units [CTL §5.5]
 *     u16 meleeChargeYaw        short
 *     i32 viewTime              antilag time on the server timeline [NET §7.1]
 *
 * ---- SNAP (server -> client) [NET §6.2] — v1: full, quantized, no delta ----------------------
 *   u8  type = 0x81
 *   u32 tick, u32 serverTime, i32 lastExecutedCmd, u8 flags
 *   PlayerState: u8 fieldCount (= PS_SCHEMA.length, version check), presence bitmask
 *     ceil(fieldCount/8) bytes, then every present field at its PS_SCHEMA wire type. A field is
 *     omitted when all its elements are +0 ("zero suppression" — stateless, so no baseline/ack
 *     bookkeeping; the decoded state is bit-identical to the quantized server state [NET §6.3]).
 *   u8 nPlayers, per player (32 B): u8 clientNum, u8 spawnCount, i16 origin[3] (quantized vs
 *     mapCenter * posScale), u16 pitch, u16 yaw (shorts), i16 velocity[3] (u/s), u8 stance,
 *     u16 viewHeight*16, u8 moveFlags, u16 eFlags, u8 weapon, u8 weaponState, u16 legsAnim,
 *     u16 torsoAnim, u16 eventSeq
 *   u8 nMissiles, per missile (22 B, +2 when armed): u16 entId, u8 kind, u8 owner, u16 fireSeq,
 *     u8 state | 0x80 if explodeAt follows, u8 parent, i16 origin[3] (world: quantized vs
 *     mapCenter; STUCK_PLAYER: offset * posScale in the parent frame, see entities.ts),
 *     FLYING: i16 velocity[3] (angles are rebuilt from it) | at rest: u16 angles[3],
 *     [i16 explodeAt - serverTime], u16 age = serverTime - spawnTime (clamped 0..65535)
 *   u16 nEvents, per event: u8 type, u8 payloadLen, payload (EVENT_SCHEMA below; first field is
 *     always i16 time - serverTime). Unknown types are skipped by length (forward compatible).
 *
 * Budget check (protocol.test.ts): 8 players + 8 missiles + a few events ≈ 0.5 KB [NET §6.2].
 */
import { MAX_CMDS_PER_PACKET } from '../constants';
import { angleToShort, shortToAngle, shortToSigned, vectorToAngles, type Vec3 } from '../math';
import { PS_SCHEMA, createPlayerState, type PsFieldType } from '../playerstate';
import {
  GameEventType, MissileState,
  type GameEvent, type MissileEntityState, type PlayerEntityState, type PlayerState, type Snapshot, type UserCmd,
  type WeaponId, type Stance, type MissileKind, type WeaponState,
} from '../types';
import { ByteReader, ByteWriter, readStr, writeStr } from './bytes';
import { MsgType } from './ids';

export interface CmdPacket {
  /** Tick of the newest snapshot the client fully received (ping + delta baseline). */
  ackSnapTick: number;
  /** Consecutive cmds; cmds[0].number = firstCmdNumber. */
  cmds: UserCmd[];
}

/** Quantization context for remote entity positions (from the `load` message). */
export interface PosQuant {
  mapCenter: [number, number, number];
  posScale: number;
}

/** First byte of a binary message. */
export function peekMsgType(data: ArrayBuffer | Uint8Array): number {
  const u8 = data instanceof Uint8Array ? data : new Uint8Array(data);
  return u8.length ? u8[0] : -1;
}

// =============================================================================================
// Quantization helpers (exported for tests and the client)
// =============================================================================================
const I16_MAX = 32767;
function clampI16(v: number): number {
  return v > I16_MAX ? I16_MAX : v < -I16_MAX ? -I16_MAX : v;
}
/** World position component -> i16 (saturating). */
export function quantPos(v: number, center: number, scale: number): number {
  return clampI16(Math.round((v - center) * scale));
}
export function dequantPos(q: number, center: number, scale: number): number {
  return q / scale + center;
}
/** Angle (deg) -> short -> angle in (-180, 180]. Matches what the server sends for remote players. */
export function quantAngle(deg: number): number {
  return shortToAngle(shortToSigned(angleToShort(deg)));
}

// =============================================================================================
// CMD
// =============================================================================================
const cmdWriter = new ByteWriter(256);
export const CMD_HEADER_BYTES = 10;
export const CMD_BYTES = 24;

export function encodeCmdPacket(p: CmdPacket): ArrayBuffer {
  const n = p.cmds.length;
  if (n < 1 || n > MAX_CMDS_PER_PACKET) throw new RangeError(`cmd packet count ${n}`);
  const w = cmdWriter.reset();
  w.u8(MsgType.CMD);
  w.u32(p.ackSnapTick);
  w.u32(p.cmds[0].number);
  w.u8(n);
  for (let i = 0; i < n; i++) {
    const c = p.cmds[i];
    if (c.number !== p.cmds[0].number + i) throw new RangeError('cmds in a packet must be consecutive');
    w.i32(c.serverTime);
    w.u32(c.buttons);
    w.u16(c.angles[0]); w.u16(c.angles[1]); w.u16(c.angles[2]);
    w.i8(Math.max(-127, Math.min(127, c.forwardmove | 0)));
    w.i8(Math.max(-127, Math.min(127, c.rightmove | 0)));
    w.u8(c.weapon);
    w.u8(Math.max(0, Math.min(255, c.meleeChargeDist | 0)));
    w.u16(c.meleeChargeYaw);
    w.i32(c.viewTime);
  }
  return w.finish();
}

export function decodeCmdPacket(data: ArrayBuffer | Uint8Array): CmdPacket {
  const r = new ByteReader(data);
  if (r.u8() !== MsgType.CMD) throw new Error('not a CMD packet');
  const ackSnapTick = r.u32();
  const first = r.u32();
  const n = r.u8();
  if (n < 1 || n > MAX_CMDS_PER_PACKET) throw new RangeError(`cmd packet count ${n}`);
  const cmds: UserCmd[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const serverTime = r.i32();
    const buttons = r.u32();
    const a0 = r.u16(), a1 = r.u16(), a2 = r.u16();
    const forwardmove = r.i8(), rightmove = r.i8();
    const weapon = r.u8() as WeaponId;
    const meleeChargeDist = r.u8();
    const meleeChargeYaw = r.u16();
    const viewTime = r.i32();
    cmds[i] = {
      number: (first + i) >>> 0, serverTime, buttons, angles: [a0, a1, a2], forwardmove, rightmove, weapon,
      viewTime, meleeChargeYaw, meleeChargeDist,
    };
  }
  return { ackSnapTick, cmds };
}

// =============================================================================================
// PlayerState (schema-driven, zero-suppressed) [NET §6.3]
// =============================================================================================
function writeTyped(w: ByteWriter, t: PsFieldType, v: number): void {
  switch (t) {
    case 'f32': w.f32(v); break;
    case 'i32': w.i32(v); break;
    case 'u32': w.u32(v); break;
    case 'i16': w.i16(v); break;
    case 'u16': w.u16(v); break;
    case 'u8': w.u8(v); break;
  }
}
function readTyped(r: ByteReader, t: PsFieldType): number {
  switch (t) {
    case 'f32': return r.f32();
    case 'i32': return r.i32();
    case 'u32': return r.u32();
    case 'i16': return r.i16();
    case 'u16': return r.u16();
    case 'u8': return r.u8();
  }
}
/** +0 is the only value that may be omitted (-0 and NaN keep their bits). */
const isPlusZero = (v: number) => v === 0 && 1 / v > 0;

export function writePlayerState(w: ByteWriter, ps: PlayerState): void {
  const p = ps as unknown as Record<string, number | number[]>;
  const nf = PS_SCHEMA.length;
  w.u8(nf);
  const maskAt = w.pos;
  const maskBytes = (nf + 7) >> 3;
  for (let i = 0; i < maskBytes; i++) w.u8(0);
  const mask = new Uint8Array(maskBytes);
  for (let i = 0; i < nf; i++) {
    const [name, type, count] = PS_SCHEMA[i];
    const v = p[name];
    let present = false;
    if (count === 1) present = !isPlusZero(v as number);
    else for (let k = 0; k < count && !present; k++) present = !isPlusZero((v as number[])[k]);
    if (!present) continue;
    mask[i >> 3] |= 1 << (i & 7);
    if (count === 1) writeTyped(w, type, v as number);
    else for (let k = 0; k < count; k++) writeTyped(w, type, (v as number[])[k]);
  }
  for (let i = 0; i < maskBytes; i++) w.patchU8(maskAt + i, mask[i]);
}

export function readPlayerState(r: ByteReader, out: PlayerState = createPlayerState()): PlayerState {
  const nf = r.u8();
  if (nf !== PS_SCHEMA.length) throw new Error(`PlayerState schema mismatch: wire ${nf} fields, local ${PS_SCHEMA.length} (client/server version skew)`);
  const mask = r.bytes((nf + 7) >> 3);
  const p = out as unknown as Record<string, number | number[]>;
  for (let i = 0; i < nf; i++) {
    const [name, type, count] = PS_SCHEMA[i];
    const present = (mask[i >> 3] & (1 << (i & 7))) !== 0;
    if (count === 1) p[name] = present ? readTyped(r, type) : 0;
    else {
      const a = p[name] as number[];
      for (let k = 0; k < count; k++) a[k] = present ? readTyped(r, type) : 0;
    }
  }
  return out;
}

/** Standalone PlayerState encode (tests, desync dumps, determinism hashes). */
const psWriter = new ByteWriter(512);
export function encodePlayerState(ps: PlayerState): Uint8Array {
  const w = psWriter.reset();
  writePlayerState(w, ps);
  return new Uint8Array(w.finish());
}
export function decodePlayerState(data: Uint8Array): PlayerState {
  return readPlayerState(new ByteReader(data));
}

// =============================================================================================
// Players / missiles
// =============================================================================================
function writePos(w: ByteWriter, v: Vec3, q: PosQuant): void {
  w.i16(quantPos(v[0], q.mapCenter[0], q.posScale));
  w.i16(quantPos(v[1], q.mapCenter[1], q.posScale));
  w.i16(quantPos(v[2], q.mapCenter[2], q.posScale));
}
function readPos(r: ByteReader, q: PosQuant): Vec3 {
  const x = r.i16(), y = r.i16(), z = r.i16();
  return [dequantPos(x, q.mapCenter[0], q.posScale), dequantPos(y, q.mapCenter[1], q.posScale), dequantPos(z, q.mapCenter[2], q.posScale)];
}
function writeVel(w: ByteWriter, v: Vec3): void {
  w.i16(clampI16(Math.round(v[0]))); w.i16(clampI16(Math.round(v[1]))); w.i16(clampI16(Math.round(v[2])));
}
function readVel(r: ByteReader): Vec3 {
  return [r.i16(), r.i16(), r.i16()];
}
function writeAngles(w: ByteWriter, a: Vec3): void {
  w.u16(angleToShort(a[0])); w.u16(angleToShort(a[1])); w.u16(angleToShort(a[2]));
}
function readAngle(r: ByteReader): number {
  return shortToAngle(shortToSigned(r.u16()));
}

export const PLAYER_ENTITY_BYTES = 32;

function writePlayer(w: ByteWriter, p: PlayerEntityState, q: PosQuant): void {
  w.u8(p.clientNum);
  w.u8(p.spawnCount);
  writePos(w, p.origin, q);
  w.u16(angleToShort(p.angles[0]));
  w.u16(angleToShort(p.angles[1]));
  writeVel(w, p.velocity);
  w.u8(p.stance);
  w.u16(Math.max(0, Math.min(0xffff, Math.round(p.viewHeight * 16))));
  w.u8(p.moveFlags);
  w.u16(p.eFlags); // EF bits are all < 0x10000 (asserted in protocol.test.ts)
  w.u8(p.weapon);
  w.u8(p.weaponState);
  w.u16(p.legsAnim);
  w.u16(p.torsoAnim);
  w.u16(p.eventSeq);
}
function readPlayer(r: ByteReader, q: PosQuant): PlayerEntityState {
  const clientNum = r.u8();
  const spawnCount = r.u8();
  const origin = readPos(r, q);
  const pitch = readAngle(r);
  const yaw = readAngle(r);
  const velocity = readVel(r);
  const stance = r.u8() as Stance;
  const viewHeight = r.u16() / 16;
  const moveFlags = r.u8();
  const eFlags = r.u16();
  const weapon = r.u8() as WeaponId;
  const weaponState = r.u8() as WeaponState;
  const legsAnim = r.u16();
  const torsoAnim = r.u16();
  const eventSeq = r.u16();
  return {
    clientNum, spawnCount, origin, angles: [pitch, yaw, 0], velocity, stance, viewHeight, moveFlags, eFlags,
    weapon, weaponState, legsAnim, torsoAnim, eventSeq,
  };
}

/** High bit of the missile state byte: explodeAt follows. */
const MS_EXPLODE = 0x80;
function writeMissile(w: ByteWriter, m: MissileEntityState, serverTime: number, q: PosQuant): void {
  w.u16(m.entId);
  w.u8(m.kind);
  w.u8(m.owner);
  w.u16(m.fireSeq);
  w.u8((m.state & 0x7f) | (m.explodeAt ? MS_EXPLODE : 0));
  w.u8(m.parent);
  if (m.state === MissileState.STUCK_PLAYER) {
    // parent-frame offset (entities.ts): quantized with the same scale, no centre
    w.i16(clampI16(Math.round(m.origin[0] * q.posScale)));
    w.i16(clampI16(Math.round(m.origin[1] * q.posScale)));
    w.i16(clampI16(Math.round(m.origin[2] * q.posScale)));
  } else writePos(w, m.origin, q);
  // flying: velocity only (the client orients the missile along it); at rest: angles only
  if (m.state === MissileState.FLYING) writeVel(w, m.velocity);
  else writeAngles(w, m.angles);
  if (m.explodeAt) w.i16(clampI16(m.explodeAt - serverTime));
  w.u16(Math.max(0, Math.min(0xffff, serverTime - m.spawnTime)));
}
const tmpAngles: Vec3 = [0, 0, 0];
function readMissile(r: ByteReader, serverTime: number, q: PosQuant): MissileEntityState {
  const entId = r.u16();
  const kind = r.u8() as MissileKind;
  const owner = r.u8();
  const fireSeq = r.u16();
  const sb = r.u8();
  const state = (sb & 0x7f) as MissileState;
  const parent = r.u8();
  let origin: Vec3;
  if (state === MissileState.STUCK_PLAYER) origin = [r.i16() / q.posScale, r.i16() / q.posScale, r.i16() / q.posScale];
  else origin = readPos(r, q);
  let velocity: Vec3, angles: Vec3;
  if (state === MissileState.FLYING) {
    velocity = readVel(r);
    vectorToAngles(tmpAngles, velocity);
    angles = [quantAngle(tmpAngles[0]), quantAngle(tmpAngles[1]), 0];
  } else {
    velocity = [0, 0, 0];
    angles = [readAngle(r), readAngle(r), readAngle(r)];
  }
  const explodeAt = sb & MS_EXPLODE ? serverTime + r.i16() : 0;
  const spawnTime = serverTime - r.u16();
  return { entId, kind, owner, fireSeq, state, parent, origin, velocity, angles, explodeAt, spawnTime };
}

// =============================================================================================
// Events [NET §6.5] — table-driven so every GameEvent variant is covered (test enforces it)
// =============================================================================================
/**
 * Field kinds: ent = entity/client number as u16 (-1 <-> 0xffff); pos = world position (3 x i16
 * quantized like players); posN = u8 flag + pos (nullable); dir = unit vector as 3 x i8 (*127);
 * yaw = degrees as a u16 short (decoded to (-180,180]); str = u8 length + UTF-8 (<= 200 bytes);
 * bool = u8.
 */
type FieldKind = 'u8' | 'u16' | 'i16' | 'i32' | 'u32' | 'ent' | 'pos' | 'posN' | 'dir' | 'yaw' | 'str' | 'bool';
type EvOf<T extends GameEventType> = Extract<GameEvent, { type: T }>;
type EvSchema<T extends GameEventType> = readonly (readonly [Exclude<keyof EvOf<T>, 'type' | 'time'>, FieldKind])[];

export const EVENT_SCHEMA: { readonly [T in GameEventType]: EvSchema<T> } = {
  [GameEventType.ENT_EVENT]: [['ent', 'ent'], ['ev', 'u8'], ['parm', 'u16'], ['seq', 'u16']],
  [GameEventType.KILL]: [['killer', 'ent'], ['victim', 'ent'], ['weapon', 'u8'], ['mod', 'u8'], ['flags', 'u8']],
  [GameEventType.DAMAGE_TAKEN]: [['attacker', 'ent'], ['amount', 'u16'], ['dirYaw', 'yaw']],
  [GameEventType.HITMARKER]: [['victim', 'ent'], ['flags', 'u8']],
  [GameEventType.EXPLOSION]: [['pos', 'pos'], ['weapon', 'u8'], ['owner', 'ent']],
  [GameEventType.IMPACT]: [['pos', 'pos'], ['normal', 'dir'], ['surface', 'u8'], ['weapon', 'u8']],
  [GameEventType.MISSILE_STICK]: [['entId', 'u16'], ['pos', 'pos'], ['normal', 'dir'], ['surface', 'u8'], ['weapon', 'u8'], ['stuckTo', 'ent']],
  [GameEventType.MISSILE_BOUNCE]: [['entId', 'u16'], ['pos', 'pos'], ['surface', 'u8'], ['weapon', 'u8']],
  [GameEventType.MELEE_HIT]: [['attacker', 'ent'], ['victim', 'ent'], ['weapon', 'u8']],
  [GameEventType.PAIN]: [['ent', 'ent'], ['health', 'i16']],
  [GameEventType.DEATH]: [['ent', 'ent'], ['mod', 'u8'], ['weapon', 'u8'], ['dirYaw', 'yaw']],
  [GameEventType.SPAWN]: [['ent', 'ent']],
  [GameEventType.PICKUP]: [['ent', 'ent'], ['weapon', 'u8']],
  [GameEventType.SCORE]: [['ent', 'ent'], ['points', 'i32'], ['kills', 'u16'], ['deaths', 'u16'], ['tomahawks', 'u16'], ['humiliated', 'u16']],
  [GameEventType.SCORE_POPUP]: [['amount', 'i16']],
  [GameEventType.MEDAL]: [['medal', 'u8']],
  [GameEventType.BANKRUPT]: [['victim', 'ent'], ['attacker', 'ent']],
  [GameEventType.ANNOUNCER]: [['alias', 'str']],
  [GameEventType.SOUND]: [['alias', 'str'], ['pos', 'posN'], ['ent', 'ent']],
  [GameEventType.MATCH]: [['phase', 'u8'], ['timeLeftMs', 'u32']],
  [GameEventType.SPLASH]: [['pos', 'pos']],
  [GameEventType.TOP3]: [['entered', 'bool']],
  // gametype agent (CONTRACT-REQUESTS gametype #1): killcam timing
  [GameEventType.KILLCAM]: [['attacker', 'ent'], ['victim', 'ent'], ['weapon', 'u8'], ['mod', 'u8'], ['deathTime', 'u32'], ['camMs', 'u16'], ['postMs', 'u16'], ['final', 'bool']],
};

function writeField(w: ByteWriter, kind: FieldKind, v: unknown, q: PosQuant): void {
  switch (kind) {
    case 'u8': w.u8(v as number); break;
    case 'u16': w.u16(v as number); break;
    case 'i16': w.i16(clampI16(Math.round(v as number))); break;
    case 'i32': w.i32(v as number); break;
    case 'u32': w.u32(v as number); break;
    case 'ent': w.u16((v as number) < 0 ? 0xffff : (v as number)); break;
    case 'pos': writePos(w, v as Vec3, q); break;
    case 'posN': if (v) { w.u8(1); writePos(w, v as Vec3, q); } else w.u8(0); break;
    case 'dir': {
      const d = v as Vec3;
      w.i8(Math.round(Math.max(-1, Math.min(1, d[0])) * 127));
      w.i8(Math.round(Math.max(-1, Math.min(1, d[1])) * 127));
      w.i8(Math.round(Math.max(-1, Math.min(1, d[2])) * 127));
      break;
    }
    case 'yaw': w.u16(angleToShort(v as number)); break;
    case 'str': writeStr(w, String(v ?? '')); break;
    case 'bool': w.u8(v ? 1 : 0); break;
  }
}
function readField(r: ByteReader, kind: FieldKind, q: PosQuant): unknown {
  switch (kind) {
    case 'u8': return r.u8();
    case 'u16': return r.u16();
    case 'i16': return r.i16();
    case 'i32': return r.i32();
    case 'u32': return r.u32();
    case 'ent': { const e = r.u16(); return e === 0xffff ? -1 : e; }
    case 'pos': return readPos(r, q);
    case 'posN': return r.u8() ? readPos(r, q) : null;
    case 'dir': return [r.i8() / 127, r.i8() / 127, r.i8() / 127];
    case 'yaw': return readAngle(r);
    case 'str': return readStr(r);
    case 'bool': return r.u8() !== 0;
  }
}

export function writeEvent(w: ByteWriter, e: GameEvent, serverTime: number, q: PosQuant): void {
  const schema = EVENT_SCHEMA[e.type] as EvSchema<GameEventType> | undefined;
  if (!schema) throw new Error(`no wire schema for GameEvent type ${e.type}`);
  w.u8(e.type);
  const lenAt = w.pos;
  w.u8(0);
  const start = w.pos;
  w.i16(clampI16(e.time - serverTime));
  const rec = e as unknown as Record<string, unknown>;
  for (const [name, kind] of schema) writeField(w, kind, rec[name as string], q);
  const len = w.pos - start;
  if (len > 255) throw new RangeError(`event ${e.type} payload ${len} > 255 bytes`);
  w.patchU8(lenAt, len);
}

/** Returns null for unknown event types (skipped by length). */
export function readEvent(r: ByteReader, serverTime: number, q: PosQuant): GameEvent | null {
  const type = r.u8();
  const len = r.u8();
  const schema = EVENT_SCHEMA[type as GameEventType] as EvSchema<GameEventType> | undefined;
  if (!schema) { r.skip(len); return null; }
  const end = r.pos + len;
  const ev: Record<string, unknown> = { type, time: serverTime + r.i16() };
  for (const [name, kind] of schema) ev[name as string] = readField(r, kind, q);
  if (r.pos !== end) throw new Error(`event ${type}: payload length mismatch (${r.pos - (end - len)} != ${len})`);
  return ev as unknown as GameEvent;
}

// =============================================================================================
// SNAP
// =============================================================================================
const snapWriter = new ByteWriter(2048);

export function encodeSnapshot(s: Snapshot, q: PosQuant): ArrayBuffer {
  const w = snapWriter.reset();
  w.u8(MsgType.SNAP);
  w.u32(s.tick);
  w.u32(s.serverTime);
  w.i32(s.lastExecutedCmd);
  w.u8(s.flags);
  writePlayerState(w, s.ps);
  const np = Math.min(255, s.players.length);
  w.u8(np);
  for (let i = 0; i < np; i++) writePlayer(w, s.players[i], q);
  const nm = Math.min(255, s.missiles.length);
  w.u8(nm);
  for (let i = 0; i < nm; i++) writeMissile(w, s.missiles[i], s.serverTime, q);
  const ne = Math.min(0xffff, s.events.length);
  w.u16(ne);
  for (let i = 0; i < ne; i++) writeEvent(w, s.events[i], s.serverTime, q);
  return w.finish();
}

export function decodeSnapshot(data: ArrayBuffer | Uint8Array, q: PosQuant): Snapshot {
  const r = new ByteReader(data);
  if (r.u8() !== MsgType.SNAP) throw new Error('not a SNAP packet');
  const tick = r.u32();
  const serverTime = r.u32();
  const lastExecutedCmd = r.i32();
  const flags = r.u8();
  const ps = readPlayerState(r);
  const np = r.u8();
  const players: PlayerEntityState[] = new Array(np);
  for (let i = 0; i < np; i++) players[i] = readPlayer(r, q);
  const nm = r.u8();
  const missiles: MissileEntityState[] = new Array(nm);
  for (let i = 0; i < nm; i++) missiles[i] = readMissile(r, serverTime, q);
  const ne = r.u16();
  const events: GameEvent[] = [];
  for (let i = 0; i < ne; i++) {
    const e = readEvent(r, serverTime, q);
    if (e) events.push(e);
  }
  return { tick, serverTime, lastExecutedCmd, flags, ps, players, missiles, events };
}
